import dayjs from 'dayjs';

import { HOURS_IN_DAY } from '@cast/constants';
import { PriceType } from '@cast/shared-types';

import {
  ComputeSpendChartData,
  CostReportChartDataByPeriod,
  CostReportChartType,
} from 'features/cost-report/types/costOverTime';
import {
  getDataHoursMultiplier,
  getUptimeMultiplier,
} from 'features/cost-report/utils/costOverTime';
import { findNearestDataPointToTimestamp } from 'utils/data/findNearestDataPointToTimestamp';

type ProcessDailyCostChartDataParams = {
  current: {
    chartData: CostReportChartDataByPeriod[];
    isLoading: boolean;
  };
  projectEndOfDayCost: boolean;
  applyUptime: boolean;
  storageCostGranularity: PriceType;
  anomalies: Array<{ timestamp?: string }>;
  chartType: CostReportChartType;
  timezone?: string;
};

type ProcessDailyCostChartDataResult = {
  isLoading: boolean;
  data: ComputeSpendChartData[];
};

const mapToSelectedRateCost = (
  datapoint: CostReportChartDataByPeriod,
  params: {
    chartType: CostReportChartType;
    timezone?: string;
  }
): ComputeSpendChartData => {
  const { chartType, timezone } = params;

  const multiplier =
    chartType === CostReportChartType.BAR
      ? getDataHoursMultiplier(datapoint, PriceType.DAILY, timezone)
      : 24;

  const remainingHours = 24 - multiplier;

  let onDemand: number | null = null;
  let onDemandForecast: number | null = null;
  let onDemandForecastDelta: number | null = null;
  let fallback: number | null = null;
  let fallbackForecast: number | null = null;
  let fallbackForecastDelta: number | null = null;
  let spot: number | null = null;
  let spotForecast: number | null = null;
  let spotForecastDelta: number | null = null;
  let storage: number | null = null;
  let storageForecast: number | null = null;
  let storageForecastDelta: number | null = null;

  if (datapoint.onDemandCost !== null) {
    onDemand = datapoint.onDemandCost * multiplier;
    onDemandForecast = datapoint.onDemandCost * 24;
    onDemandForecastDelta =
      remainingHours > 0 ? datapoint.onDemandCost * remainingHours : null;
  }

  if (datapoint.fallbackCost !== null) {
    fallback = datapoint.fallbackCost * multiplier;
    fallbackForecast = datapoint.fallbackCost * 24;
    fallbackForecastDelta =
      remainingHours > 0 ? datapoint.fallbackCost * remainingHours : null;
  }

  if (datapoint.spotCost !== null) {
    spot = datapoint.spotCost * multiplier;
    spotForecast = datapoint.spotCost * 24;
    spotForecastDelta =
      remainingHours > 0 ? datapoint.spotCost * remainingHours : null;
  }

  if (datapoint.totalStorageCost !== null) {
    storage = datapoint.totalStorageCost * multiplier;
    storageForecast = datapoint.totalStorageCost * 24;
    storageForecastDelta =
      remainingHours > 0 ? datapoint.totalStorageCost * remainingHours : null;
  }

  return {
    timestamp: datapoint.timestamp,
    forecast: datapoint.forecast,
    onDemand,
    fallback,
    spot,
    storage,
    onDemandForecast,
    fallbackForecast,
    spotForecast,
    storageForecast,
    onDemandForecastDelta,
    fallbackForecastDelta,
    spotForecastDelta,
    storageForecastDelta,
    onDemandCpuCount: datapoint.onDemandCpuCount,
    fallbackCpuCount: datapoint.fallbackCpuCount,
    spotCpuCount: datapoint.spotCpuCount,
    fallbackRamGib: datapoint.fallbackRamGib,
    onDemandRamGib: datapoint.onDemandRamGib,
    spotRamGib: datapoint.spotRamGib,
    anomalies: [],
  };
};

const mapToUptimeCost = (
  datapoint: CostReportChartDataByPeriod,
  params: {
    applyUptime: boolean;
    storageCostGranularity: PriceType;
  }
): ComputeSpendChartData => {
  const { applyUptime, storageCostGranularity } = params;

  const multiplier = applyUptime
    ? getUptimeMultiplier(datapoint)
    : { onDemand: 1, spot: 1, fallback: 1 };

  let onDemand: number | null = null;
  let fallback: number | null = null;
  let spot: number | null = null;
  let storage: number | null = null;

  if (datapoint.onDemandCost !== null) {
    onDemand = datapoint.onDemandCost * multiplier.onDemand;
  }

  if (datapoint.fallbackCost !== null) {
    fallback = datapoint.fallbackCost * multiplier.fallback;
  }

  if (datapoint.spotCost !== null) {
    spot = datapoint.spotCost * multiplier.spot;
  }

  if (datapoint.totalStorageCost !== null) {
    storage =
      storageCostGranularity === PriceType.HOURLY
        ? datapoint.totalStorageCost * HOURS_IN_DAY
        : datapoint.totalStorageCost;
  }

  return {
    timestamp: datapoint.timestamp,
    onDemand,
    fallback,
    spot,
    storage,
    onDemandRamGib: datapoint.onDemandRamGib,
    fallbackRamGib: datapoint.fallbackCost,
    spotRamGib: datapoint.spotRamGib,
    onDemandCpuCount: datapoint.onDemandCpuCount,
    fallbackCpuCount: datapoint.fallbackCpuCount,
    spotCpuCount: datapoint.spotCpuCount,
    forecast: datapoint.forecast,
    anomalies: [],
  };
};

export const processDailyCostChartData = (
  params: ProcessDailyCostChartDataParams
): ProcessDailyCostChartDataResult => {
  const { current, projectEndOfDayCost, anomalies, timezone } = params;

  const mappedData = current.chartData.map((data) =>
    projectEndOfDayCost
      ? mapToSelectedRateCost(data, { ...params, timezone })
      : mapToUptimeCost(data, params)
  );

  anomalies.forEach((anomaly) => {
    const pointToMark = findNearestDataPointToTimestamp(
      mappedData,
      dayjs(anomaly.timestamp)
    );
    if (pointToMark) {
      pointToMark.anomalies.push(anomaly);
    }
  });

  return {
    isLoading: current.isLoading,
    data: mappedData,
  };
};
